import type { SubscriptionPlan, CompanySubscription } from '@/features/settings/types/settings.types';
import {
  type PlanTier,
  type SubscriptionFeature,
  type FeatureAccess,
  type CreationAccess,
  PLAN_LIMITS,
  FEATURE_REQUIRED_PLAN,
} from './types';

/**
 * Determine the plan tier from a subscription plan
 */
export function getPlanTierFromPlan(plan: SubscriptionPlan | null): PlanTier {
  if (!plan) return 'free';

  const planName = plan.name.toLowerCase();

  if (planName.includes('enterprise')) return 'enterprise';
  if (planName.includes('ultim')) return 'ultim';
  if (planName.includes('medium') || planName.includes('pro')) return 'medium';

  // Fallback to checking limits
  if (plan.maxShops >= 1000) return 'enterprise';
  if (plan.maxShops >= 10) return 'ultim';
  if (plan.maxShops >= 3) return 'medium';

  return 'free';
}

/**
 * Get the plan tier from a CompanySubscription
 */
export function getPlanTierFromSubscription(subscription: CompanySubscription | null): PlanTier {
  if (!subscription) return 'free';
  return getPlanTierFromPlan(subscription.plan);
}

/**
 * Get the plan tier order for comparison
 */
export function getPlanTierOrder(tier: PlanTier): number {
  const order: Record<PlanTier, number> = {
    free: 0,
    medium: 1,
    ultim: 2,
    enterprise: 3,
  };
  return order[tier];
}

/**
 * Check if a plan tier meets the minimum required tier
 */
export function meetsRequiredTier(current: PlanTier, required: PlanTier): boolean {
  return getPlanTierOrder(current) >= getPlanTierOrder(required);
}

/**
 * Get feature access status
 */
export function getFeatureAccess(
  feature: SubscriptionFeature,
  subscription: CompanySubscription | null,
  currentCounts?: { shops: number; products: number }
): FeatureAccess {
  const planTier = getPlanTierFromSubscription(subscription);
  const requiredPlan = FEATURE_REQUIRED_PLAN[feature];
  const hasAccess = meetsRequiredTier(planTier, requiredPlan);

  const result: FeatureAccess = {
    hasAccess,
    requiredPlan,
  };

  const plan = subscription?.plan;

  // Add limits for countable features
  if (feature === 'shops' && plan) {
    result.limit = plan.maxShops;
    if (currentCounts) {
      result.current = currentCounts.shops;
      result.remaining = Math.max(0, plan.maxShops - currentCounts.shops);
    }
  }

  if (feature === 'products' && plan) {
    result.limit = plan.maxProducts;
    if (currentCounts) {
      result.current = currentCounts.products;
      result.remaining = Math.max(0, plan.maxProducts - currentCounts.products);
    }
  }

  return result;
}

/**
 * Check if user can create a new shop
 */
export function canCreateShop(
  subscription: CompanySubscription | null,
  currentShopsCount: number
): CreationAccess {
  const plan = subscription?.plan;

  if (!plan) {
    return {
      canCreate: currentShopsCount < PLAN_LIMITS.free.maxShops,
      remaining: Math.max(0, PLAN_LIMITS.free.maxShops - currentShopsCount),
      limit: PLAN_LIMITS.free.maxShops,
      current: currentShopsCount,
    };
  }

  return {
    canCreate: currentShopsCount < plan.maxShops,
    remaining: Math.max(0, plan.maxShops - currentShopsCount),
    limit: plan.maxShops,
    current: currentShopsCount,
  };
}

/**
 * Check if user can create a new product
 */
export function canCreateProduct(
  subscription: CompanySubscription | null,
  currentProductsCount: number
): CreationAccess {
  const plan = subscription?.plan;

  if (!plan) {
    return {
      canCreate: currentProductsCount < PLAN_LIMITS.free.maxProducts,
      remaining: Math.max(0, PLAN_LIMITS.free.maxProducts - currentProductsCount),
      limit: PLAN_LIMITS.free.maxProducts,
      current: currentProductsCount,
    };
  }

  return {
    canCreate: currentProductsCount < plan.maxProducts,
    remaining: Math.max(0, plan.maxProducts - currentProductsCount),
    limit: plan.maxProducts,
    current: currentProductsCount,
  };
}

/**
 * Check if user can manage employees
 */
export function canManageEmployees(subscription: CompanySubscription | null): boolean {
  if (!subscription) return false;
  return subscription.hasMultipleEmployeeAccounts;
}

/**
 * Check if user has ads access
 */
export function hasAdsAccess(subscription: CompanySubscription | null): boolean {
  if (!subscription) return false;
  return subscription.hasAdsAccess;
}

/**
 * Check if user has price comparison access
 */
export function hasPriceComparisonAccess(subscription: CompanySubscription | null): boolean {
  if (!subscription) return false;
  return subscription.hasPriceComparison;
}

/**
 * Check if user can delete shops
 */
export function canDeleteShops(subscription: CompanySubscription | null): boolean {
  if (!subscription?.plan) return false;
  // Check plan tier since canDeleteShops might not be on SubscriptionPlan
  const tier = getPlanTierFromPlan(subscription.plan);
  return PLAN_LIMITS[tier].canDeleteShops;
}

/**
 * Check if user can delete products
 */
export function canDeleteProducts(subscription: CompanySubscription | null): boolean {
  if (!subscription?.plan) return false;
  // Check plan tier since canDeleteProducts might not be on SubscriptionPlan
  const tier = getPlanTierFromPlan(subscription.plan);
  return PLAN_LIMITS[tier].canDeleteProducts;
}

/**
 * Get metrics level
 */
export function getMetricsLevel(subscription: CompanySubscription | null): number {
  if (!subscription?.plan) return PLAN_LIMITS.free.metricsLevel;
  return subscription.plan.metricsLevel;
}

/**
 * Check if user has advanced metrics
 */
export function hasAdvancedMetrics(subscription: CompanySubscription | null): boolean {
  if (!subscription) return false;
  return subscription.hasAdvancedMetrics;
}

/**
 * Get the display name for a plan tier
 */
export function getPlanTierDisplayName(tier: PlanTier): string {
  const names: Record<PlanTier, string> = {
    free: 'Free',
    medium: 'Medium',
    ultim: 'Ultim',
    enterprise: 'Enterprise',
  };
  return names[tier];
}

/**
 * Get the next tier upgrade for a given tier
 */
export function getNextTierUpgrade(tier: PlanTier): PlanTier | null {
  const upgrades: Record<PlanTier, PlanTier | null> = {
    free: 'medium',
    medium: 'ultim',
    ultim: 'enterprise',
    enterprise: null,
  };
  return upgrades[tier];
}

/**
 * Check if subscription is active
 */
export function isSubscriptionActive(subscription: CompanySubscription | null): boolean {
  if (!subscription) return false;
  if (!subscription.isEnabled) return false;

  const endDate = new Date(subscription.endDate);
  return endDate > new Date();
}
